/**
 * Single source of truth for Contact List chips, reason labels and the route
 * paths for the Contact Lists module (UI PRD Round 3).
 */
import { ChipProps } from "@mui/material";
import {
  ContactListStatus,
  ContactStatus,
} from "../../../redux/sequenceContactLists/sequenceContactLists.types";

/* ------------------------------------------------------------------ *
 * Module access key (UI PRD §1). Role access comes from
 * Sequence → Contact Lists. The server returns 403 when the role lacks it.
 * ------------------------------------------------------------------ */
export const CONTACT_LIST_MODULE_KEY = "latest_sequence_contact_list_basic";

/** The DB view used by the sequence-builder condition pickers (UI PRD §4). */
export const CONTACT_LIST_RECORD_VIEW = "latest_sequence_contact_list";

/* ------------------------------------------------------------------ *
 * Route paths
 * ------------------------------------------------------------------ */
export const APP_SEQUENCE_CONTACT_LISTS = "/settings/email-sequences/contact-lists";
export const APP_SEQUENCE_CONTACT_LIST_DETAIL =
  "/settings/email-sequences/contact-lists/detail";

/* ------------------------------------------------------------------ *
 * List status chip
 * ------------------------------------------------------------------ */
export const CONTACT_LIST_STATUS_CHIP: Record<
  ContactListStatus,
  { label: string; color: ChipProps["color"]; textColor?: string }
> = {
  ACTIVE: { label: "Active", color: "success" },
  INACTIVE: { label: "Inactive", color: "default", textColor: "#000" },
};

export const CONTACT_LIST_STATUS_FILTER_OPTIONS = [
  { label: "All", value: "-1" },
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

/* ------------------------------------------------------------------ *
 * Contact status badge (UI PRD §2)
 * ------------------------------------------------------------------ */
export const CONTACT_STATUS_CHIP: Record<
  ContactStatus,
  { label: string; color: ChipProps["color"]; textColor?: string; tip: string }
> = {
  ACTIVE: {
    label: "Active",
    color: "success",
    tip: "Receives sequence mail",
  },
  INACTIVE: {
    label: "Inactive",
    color: "default",
    textColor: "#000",
    tip: "Paused by a user; gets no sequence mail",
  },
  UNSUBSCRIBED: {
    label: "Unsubscribed",
    color: "default",
    textColor: "#000",
    tip: "Asked not to be contacted",
  },
  BOUNCED: {
    label: "Bounced",
    color: "warning",
    textColor: "#000",
    tip: "Mail to this address bounced. Set automatically, with \u201cModified by: SendGrid bounce\u201d",
  },
};

export const CONTACT_STATUS_FILTER_OPTIONS = [
  { label: "All", value: "-1" },
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Unsubscribed", value: "UNSUBSCRIBED" },
  { label: "Bounced", value: "BOUNCED" },
];

export const CONTACT_STATUS_OPTIONS: { label: string; value: ContactStatus }[] =
  [
    { label: "Active", value: "ACTIVE" },
    { label: "Inactive", value: "INACTIVE" },
    { label: "Unsubscribed", value: "UNSUBSCRIBED" },
    { label: "Bounced", value: "BOUNCED" },
  ];

/* ------------------------------------------------------------------ *
 * Not-enrolled reason labels (UI PRD §5, reused for manual enrol §6)
 * ------------------------------------------------------------------ */
export const CONTACT_NOT_ENROLLED_LABELS: Record<string, string> = {
  already_enrolled: "Already in this sequence",
  already_enrolled_this_cycle: "Already in this sequence",
  contact_unsubscribed: "Unsubscribed",
  contact_bounced: "Bounced",
  contact_inactive: "Inactive contact",
  list_inactive: "List is inactive",
  list_missing: "List not found",
  record_missing: "Contact not found",
  not_eligible: "Matches the exit conditions",
};

/* ------------------------------------------------------------------ *
 * Contact-list stop reason labels (UI PRD §6). Merged with the base
 * sequence STOP_REASON_LABELS wherever stop_reason is shown.
 * ------------------------------------------------------------------ */
export const CONTACT_STOP_REASON_LABELS: Record<string, string> = {
  contact_unsubscribed: "Contact unsubscribed",
  contact_bounced: "Contact's email bounced",
  contact_inactive: "Contact deactivated",
};

/* ------------------------------------------------------------------ *
 * Cron-log status chip (activation progress, §5)
 * ------------------------------------------------------------------ */
export const CRON_LOG_STATUS_CHIP: Record<
  string,
  { label: string; color: ChipProps["color"]; textColor?: string }
> = {
  RUNNING: { label: "Running", color: "warning", textColor: "#000" },
  SUCCESS: { label: "Success", color: "success" },
  FAILED: { label: "Failed", color: "error" },
  SKIPPED: { label: "Skipped", color: "default", textColor: "#000" },
};
